import { Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { ContractType, ElectricityInvoice, ElectricityPODCalculationData, GasInvoice, GasPDRCalculationData, InvoiceElectricityAndCompanyGet, InvoiceElectricityPODGet, InvoiceGasAndCompanyGet, InvoiceGasPDRGet, PriceType, VisibilityStatus } from '../interfaces/invoice-interfaces';
import { DateConversionService } from './date-conversion.service';

@Injectable({
  providedIn: 'root'
})
export class InvoiceHelperService {
  constructor(private dateConversion: DateConversionService, private translocoService: TranslocoService) { }

  getInvoiceSupplierName(invoice: ElectricityInvoice | GasInvoice): string {
    const electricity = invoice as ElectricityInvoice;
    if (electricity.pods?.length) {
      for (const pod of electricity.pods) {
        const name = pod.supplier?.name?.trim();
        if (name) { return name; }
      }
    }

    const gas = invoice as GasInvoice;
    if (gas.pdrs?.length) {
      for (const pdr of gas.pdrs) {
        const name = pdr.supplier?.name?.trim();
        if (name) { return name; }
      }
    }

    return invoice.supplier_name ?? '';
  }

  getElectricityInvoiceStatus(invoice: ElectricityInvoice): boolean {
    if (invoice.visibility_status === VisibilityStatus.UNDER_ADMIN_REVIEW) {
      return false;
    }
    const summedPOD = this.getElectricityInvoicePODSummed(invoice);

    var status: boolean = false;
    try {
      status = invoice.is_consumption_ok &&
        invoice.is_taxes_ok &&
        invoice.is_invoice_amount_ok &&
        summedPOD.is_price_var_f1_ok &&
        summedPOD.is_price_var_f2_ok &&
        summedPOD.is_price_var_f3_ok &&
        summedPOD.is_price_var_f0_ok &&
        summedPOD.is_price_var_p_ok &&
        summedPOD.is_price_var_po_ok &&
        summedPOD.is_tms_charges_ok
    } catch (error) {
      return false;
    }
    return status;
  }

  getGasInvoiceStatus(invoice: GasInvoice): boolean {
    if (invoice.visibility_status === VisibilityStatus.UNDER_ADMIN_REVIEW) {
      return false;
    }
    const summedPDR = this.getGasInvoicePDRSummed(invoice);
    var status: boolean = false;
    try {
      status = invoice.is_consumption_ok! && invoice.is_taxes_ok! && invoice.is_invoice_amount_ok! && summedPDR.is_unit_prices_ok! && summedPDR.is_material_costs_ok! && summedPDR.is_distribution_costs_ok! && summedPDR.is_transportation_costs_ok!;
    } catch (error) {
      return false;
    }
    return status;
  }

  exportElectricityInvoicesToCSV(invoices: ElectricityInvoice[], filename: string = '') {
    if (!invoices || invoices.length === 0) {
      return;
    }

    const now = new Date();
    const formattedDateTime = now.toISOString().slice(0, 19).replace('T', '_').replace(/:/g, '-');

    if (!filename) {
      filename = `electricity_invoices_${formattedDateTime}.csv`;
    }


    const csvRows: string[] = [];
    const headers = [
      'For Period',
      'Invoice Number',
      'Supplier Name',
      'Total Consumption',
      'Invoice Amount',
      'Total Taxes',
      'TMS Charges',
      'Status',
      'Consumption OK',
      'Taxes OK',
      'Invoice Amount OK',
      'Reactive Energy Costs',
      'Tension Factor',
      'Dispatch Costs',
      'Energy Charge Price',
      'Power Charge Price',
      'Fixed Costs',
      'Additional Charges',
      'Total Discount Amount',
      'Total Unit Costs F1',
      'Month Consumption F1',
      'Total Unit Costs F2',
      'Month Consumption F2',
      'Total Unit Costs F3',
      'Month Consumption F3'
    ];

    csvRows.push(headers.join(','));

    invoices.forEach(invoice => {
      const summedPOD = this.getElectricityInvoicePODSummed(invoice);
      const row = [
        this.dateConversion.isoToFormattedPeriod(invoice.for_period ?? '') ?? '',
        invoice.invoice_number ?? '',
        invoice.supplier_name ?? '',
        invoice.total_consumption?.toString() ?? '',
        invoice.invoice_amount_net_actual?.toString() ?? '',
        invoice.total_taxes?.toString() ?? '',
        invoice.tms_charges?.toString() ?? '',
        invoice.status ?? '',
        invoice.is_consumption_ok.toString(),
        invoice.is_taxes_ok.toString(),
        invoice.is_invoice_amount_ok.toString(),
        summedPOD.reactive_energy_costs?.toString() ?? '',
        summedPOD.tension_factor?.toString() ?? '',
        summedPOD.dispatch_costs?.toString() ?? '',
        summedPOD.energy_charge_price?.toString() ?? '',
        summedPOD.power_charge_price?.toString() ?? '',
        summedPOD.fixed_costs?.toString() ?? '',
        summedPOD.additional_charges?.toString() ?? '',
        summedPOD.total_discount_amount?.toString() ?? '',
        summedPOD.calc_data?.total_unit_costs_f1?.toString() ?? '',
        summedPOD.month_consumption_f1?.toString() ?? '',
        summedPOD.calc_data?.total_unit_costs_f2?.toString() ?? '',
        summedPOD.month_consumption_f2?.toString() ?? '',
        summedPOD.calc_data?.total_unit_costs_f3?.toString() ?? '',
        summedPOD.month_consumption_f3?.toString() ?? ''
      ];

      csvRows.push(row.join(','));
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  }

  exportGasInvoicesToCSV(invoices: GasInvoice[], filename: string = '') {
    if (!invoices || invoices.length === 0) {
      return;
    }

    const now = new Date();
    const formattedDateTime = now.toISOString().slice(0, 19).replace('T', '_').replace(/:/g, '-');

    if (!filename) {
      filename = `gas_invoices_${formattedDateTime}.csv`;
    }

    const csvRows: string[] = [];
    const headers = [
      'For Period',
      'Invoice Number',
      'Supplier Name',
      'Total Consumption',
      'Invoice Amount',
      'Total Taxes',
      'TMS Charges',
      'Consumption OK',
      'Taxes OK',
      'Invoice Amount OK',
      'Spread',
      'Fixed Quota',
      'Forecast Index Value',
      'PDR ID',
      'PDR Region',
      'PDR Province',
      'PDR Community',
      'Tax Region',
      'Area Gas',
      'REMI Type Gas',
      'Gas Meter Class',
      'Gas Intensity Class',
      'Month Gas Consumption',
      'Gas Perc With Var Price',
      'Daily Gas Capacity',
      'Price Type Gas',
      'Variable Gas Consumption',
      'Fixed Gas Consumption',
      'Gas Market Index',
      'Gas Market Price',
      'Price Gas Var Actual',
      'Price Gas Fix',
      'HCV Gas',
      'Daily Capacity Factor Gas',
      'Fixed Charge Gas Actual',
      'Material Cost Factor',
      'Material Costs Gas Variable Actual',
      'Material Costs Gas Fixed Actual',
      'Fixed Distribution Costs Gas Calc',
      'Fixed Distribution Costs Gas Actual',
      'Variable Transportation Fee',
      'Var Transportation Costs Actual',
      'Total Var Distribution Costs Actual',
      'TMS Gas Charges Actual',
      'Exercise Tax Type Gas',
      'Tax Type Gas',
      'Taxrate Gas Scale 1',
      'Taxrate Gas Scale 2',
      'Taxrate Gas Scale 3',
      'Taxrate Gas Scale 4',
      'Total Gas Taxes Actual',
      'Additional Gas Charges',
      'Total Gas Discount Amount',
      'Unit Prices OK',
      'Material Costs OK',
      'Distribution Costs OK',
      'Transportation Costs OK',
      'TD Key',
      'Price Gas Var Calc',
      'Fixed Charge Gas Calc',
      'Material Costs Gas Variable Calc',
      'Material Costs Gas Fixed Calc',
      'Var Transportation Costs Calc',
      'Cummulated Gas Consumption',
      'Invoice Gas Amount Calc'
    ];

    csvRows.push(headers.join(','));

    invoices.forEach(invoice => {
      const summedPDR = this.getGasInvoicePDRSummed(invoice);
      const row = [
        this.dateConversion.isoToFormattedPeriod(invoice.for_period ?? '') ?? '',
        invoice.invoice_number ?? '',
        invoice.supplier_name ?? '',
        invoice.total_consumption?.toString() ?? '',
        invoice.invoice_amount_net_actual?.toString() ?? '',
        invoice.total_taxes?.toString() ?? '',
        invoice.tms_charges?.toString() ?? '',
        invoice.is_consumption_ok.toString(),
        invoice.is_taxes_ok.toString(),
        invoice.is_invoice_amount_ok.toString(),
        summedPDR.spread?.toString() ?? '',
        summedPDR.fixed_quota?.toString() ?? '',
        summedPDR.forecast_index_value?.toString() ?? '',
        summedPDR.pdr_id ?? '',
        summedPDR.pdr_region ?? '',
        summedPDR.pdr_province ?? '',
        summedPDR.pdr_community ?? '',
        summedPDR.tax_region ?? '',
        summedPDR.area_gas ?? '',
        summedPDR.REMI_type_gas ?? '',
        summedPDR.gas_meter_class ?? '',
        summedPDR.gas_intensity_class ?? '',
        summedPDR.month_gas_consumption.toString(),
        summedPDR.gas_perc_with_var_price?.toString() ?? '',
        summedPDR.daily_gas_capacity?.toString() ?? '',
        summedPDR.price_type_gas,
        summedPDR.variable_gas_consumption?.toString() ?? '',
        summedPDR.fixed_gas_consumption?.toString() ?? '',
        summedPDR.gas_market_index ?? '',
        summedPDR.gas_market_price?.toString() ?? '',
        summedPDR.price_gas_var_actual?.toString() ?? '',
        summedPDR.price_gas_fix?.toString() ?? '',
        summedPDR.hcv_gas?.toString() ?? '',
        summedPDR.daily_capacity_factor_gas?.toString() ?? '',
        summedPDR.fixed_charge_gas_actual?.toString() ?? '',
        summedPDR.material_cost_factor?.toString() ?? '',
        summedPDR.material_costs_gas_variable_actual?.toString() ?? '',
        summedPDR.material_costs_gas_fixed_actual?.toString() ?? '',
        summedPDR.fixed_distribution_costs_gas_calc?.toString() ?? '',
        summedPDR.fixed_distribution_costs_gas_actual?.toString() ?? '',
        summedPDR.variable_transportation_fee?.toString() ?? '',
        summedPDR.var_transportation_costs_actual?.toString() ?? '',
        summedPDR.total_var_distribution_costs_actual?.toString() ?? '',
        summedPDR.tms_gas_charges_actual?.toString() ?? '',
        summedPDR.exercise_tax_type_gas ?? '',
        summedPDR.tax_type_gas ?? '',
        summedPDR.taxrate_gas_scale1?.toString() ?? '',
        summedPDR.taxrate_gas_scale2?.toString() ?? '',
        summedPDR.taxrate_gas_scale3?.toString() ?? '',
        summedPDR.taxrate_gas_scale4?.toString() ?? '',
        summedPDR.total_gas_taxes_actual?.toString() ?? '',
        summedPDR.additional_gas_charges?.toString() ?? '',
        summedPDR.total_gas_discount_amount?.toString() ?? '',
        summedPDR.is_unit_prices_ok.toString(),
        summedPDR.is_material_costs_ok.toString(),
        summedPDR.is_distribution_costs_ok.toString(),
        summedPDR.is_transportation_costs_ok.toString(),
        summedPDR.calc_data?.td_key ?? '',
        summedPDR.calc_data?.price_gas_var_calc?.toString() ?? '',
        summedPDR.calc_data?.fixed_charge_gas_calc?.toString() ?? '',
        summedPDR.calc_data?.material_costs_gas_variable_calc?.toString() ?? '',
        summedPDR.calc_data?.material_costs_gas_fixed_calc?.toString() ?? '',
        summedPDR.calc_data?.var_transportation_costs_calc?.toString() ?? '',
        summedPDR.calc_data?.cummulated_gas_consumption?.toString() ?? '',
        summedPDR.calc_data?.invoice_amount_net_calc?.toString() ?? ''
      ];

      csvRows.push(row.join(','));
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  }

  getGasInvoicePDRSummed(invoice: GasInvoice): InvoiceGasPDRGet {
    var summedCalcData: GasPDRCalculationData = {
      td_key: null,
      price_gas_var_calc: 0,
      fixed_charge_gas_calc: 0,
      material_costs_gas_variable_calc: 0,
      material_costs_gas_fixed_calc: 0,
      var_transportation_costs_calc: 0,
      cummulated_gas_consumption: 0,

      consumption_tier_1: 0,
      consumption_tier_2: 0,
      consumption_tier_3: 0,
      consumption_tier_4: 0,
      consumption_tier_5: 0,
      consumption_tier_6: 0,
      consumption_tier_7: 0,
      consumption_tier_8: 0,

      var_distribution_costs_tier1: 0,
      var_distribution_costs_tier2: 0,
      var_distribution_costs_tier3: 0,
      var_distribution_costs_tier4: 0,
      var_distribution_costs_tier5: 0,
      var_distribution_costs_tier6: 0,
      var_distribution_costs_tier7: 0,
      var_distribution_costs_tier8: 0,

      total_var_distribution_costs_calc: 0,
      tms_gas_charges_calc: 0,

      tax_consumption_scale1: 0,
      tax_consumption_scale2: 0,
      tax_consumption_scale3: 0,
      tax_consumption_scale4: 0,

      total_gas_taxes_calc: 0,
      invoice_amount_net_calc: 0,
      invoice_amount_gross_calc: 0,
      vat_amount_calc: 0,
      file_dispute_reasons: null
    }

    var summed: InvoiceGasPDRGet = {
      id: null,
      spread: 0,
      fixed_quota: 0,
      forecast_index_value: 0,

      pdr_id: null,
      pdr_region: null,
      pdr_province: null,
      pdr_community: null,
      tax_region: null,
      area_gas: null,
      REMI_type_gas: null,
      gas_meter_class: null,
      gas_intensity_class: null,

      month_gas_consumption: 0,
      gas_perc_with_var_price: 0,
      daily_gas_capacity: 0,
      price_type_gas: null,
      variable_gas_consumption: 0,
      fixed_gas_consumption: 0,

      gas_market_index: null,
      gas_market_price: 0,
      price_gas_var_actual: 0,
      price_gas_fix: 0,
      hcv_gas: 0,

      daily_capacity_factor_gas: 0,
      fixed_charge_gas_actual: 0,

      material_cost_factor: 0,
      material_costs_gas_variable_actual: 0,
      material_costs_gas_fixed_actual: 0,

      fixed_distribution_costs_gas_calc: 0,
      fixed_distribution_costs_gas_actual: 0,
      variable_transportation_fee: 0,
      var_transportation_costs_actual: 0,

      var_distribution_fee_tier1: 0,
      var_distribution_fee_tier2: 0,
      var_distribution_fee_tier3: 0,
      var_distribution_fee_tier4: 0,
      var_distribution_fee_tier5: 0,
      var_distribution_fee_tier6: 0,
      var_distribution_fee_tier7: 0,
      var_distribution_fee_tier8: 0,
      total_var_distribution_costs_actual: 0,

      tms_gas_charges_actual: 0,

      exercise_tax_type_gas: null,
      expected_exercise_tax_type_gas: null,
      tax_type_gas: null,
      taxrate_gas_scale1: 0,
      taxrate_gas_scale2: 0,
      taxrate_gas_scale3: 0,
      taxrate_gas_scale4: 0,
      total_gas_taxes_actual: 0,
      additional_gas_charges: 0,
      total_gas_discount_amount: 0,

      cost_data: null,

      is_unit_prices_ok: true,
      is_material_costs_ok: true,
      is_distribution_costs_ok: true,
      is_transportation_costs_ok: true,

      calc_data: summedCalcData,
      supplier: null,
      vat_rate: 0,
      vat_amount_actual: 0,
      additional_note_fields: null
    };

    invoice.pdrs.forEach((pdr) => {
      summed.spread! += pdr.spread ?? 0;
      summed.fixed_quota! += pdr.fixed_quota ?? 0;
      summed.forecast_index_value! += pdr.forecast_index_value ?? 0;
      summed.month_gas_consumption! += pdr.month_gas_consumption ?? 0;
      summed.gas_perc_with_var_price! += pdr.gas_perc_with_var_price ?? 0;
      summed.daily_gas_capacity! += pdr.daily_gas_capacity ?? 0;
      summed.variable_gas_consumption! += pdr.variable_gas_consumption ?? 0;
      summed.fixed_gas_consumption! += pdr.fixed_gas_consumption ?? 0;
      summed.gas_market_price! += pdr.gas_market_price ?? 0;
      summed.price_gas_var_actual! += pdr.price_gas_var_actual ?? 0;
      summed.price_gas_fix! += pdr.price_gas_fix ?? 0;
      summed.hcv_gas! += pdr.hcv_gas ?? 0;
      summed.daily_capacity_factor_gas! += pdr.daily_capacity_factor_gas ?? 0;
      summed.fixed_charge_gas_actual! += pdr.fixed_charge_gas_actual ?? 0;
      summed.material_cost_factor! += pdr.material_cost_factor ?? 0;
      summed.material_costs_gas_variable_actual! += pdr.material_costs_gas_variable_actual ?? 0;
      summed.material_costs_gas_fixed_actual! += pdr.material_costs_gas_fixed_actual ?? 0;
      summed.fixed_distribution_costs_gas_calc! += pdr.fixed_distribution_costs_gas_calc ?? 0;
      summed.fixed_distribution_costs_gas_actual! += pdr.fixed_distribution_costs_gas_actual ?? 0;
      summed.variable_transportation_fee! += pdr.variable_transportation_fee ?? 0;
      summed.var_transportation_costs_actual! += pdr.var_transportation_costs_actual ?? 0;
      summed.var_distribution_fee_tier1! += pdr.var_distribution_fee_tier1 ?? 0;
      summed.var_distribution_fee_tier2! += pdr.var_distribution_fee_tier2 ?? 0;
      summed.var_distribution_fee_tier3! += pdr.var_distribution_fee_tier3 ?? 0;
      summed.var_distribution_fee_tier4! += pdr.var_distribution_fee_tier4 ?? 0;
      summed.var_distribution_fee_tier5! += pdr.var_distribution_fee_tier5 ?? 0;
      summed.var_distribution_fee_tier6! += pdr.var_distribution_fee_tier6 ?? 0;
      summed.var_distribution_fee_tier7! += pdr.var_distribution_fee_tier7 ?? 0;
      summed.var_distribution_fee_tier8! += pdr.var_distribution_fee_tier8 ?? 0;
      summed.total_var_distribution_costs_actual! += pdr.total_var_distribution_costs_actual ?? 0;
      summed.tms_gas_charges_actual! += pdr.tms_gas_charges_actual ?? 0;
      summed.taxrate_gas_scale1! += pdr.taxrate_gas_scale1 ?? 0;
      summed.taxrate_gas_scale2! += pdr.taxrate_gas_scale2 ?? 0;
      summed.taxrate_gas_scale3! += pdr.taxrate_gas_scale3 ?? 0;
      summed.taxrate_gas_scale4! += pdr.taxrate_gas_scale4 ?? 0;
      summed.total_gas_taxes_actual! += pdr.total_gas_taxes_actual ?? 0;
      summed.additional_gas_charges! += pdr.additional_gas_charges ?? 0;
      summed.total_gas_discount_amount! += pdr.total_gas_discount_amount ?? 0;

      summed.vat_rate! += pdr.vat_rate ?? 0;
      summed.vat_amount_actual! += pdr.vat_amount_actual ?? 0;

      summed.is_unit_prices_ok = summed.is_unit_prices_ok && pdr.is_unit_prices_ok;
      summed.is_material_costs_ok = summed.is_material_costs_ok && pdr.is_material_costs_ok;
      summed.is_distribution_costs_ok = summed.is_distribution_costs_ok && pdr.is_distribution_costs_ok;
      summed.is_transportation_costs_ok = summed.is_transportation_costs_ok && pdr.is_transportation_costs_ok;

      if (pdr.calc_data) {
        summed.calc_data!.price_gas_var_calc! += pdr.calc_data.price_gas_var_calc ?? 0;
        summed.calc_data!.fixed_charge_gas_calc! += pdr.calc_data.fixed_charge_gas_calc ?? 0;
        summed.calc_data!.material_costs_gas_variable_calc! += pdr.calc_data.material_costs_gas_variable_calc ?? 0;
        summed.calc_data!.material_costs_gas_fixed_calc! += pdr.calc_data.material_costs_gas_fixed_calc ?? 0;
        summed.calc_data!.var_transportation_costs_calc! += pdr.calc_data.var_transportation_costs_calc ?? 0;
        summed.calc_data!.cummulated_gas_consumption! += pdr.calc_data.cummulated_gas_consumption ?? 0;

        summed.calc_data!.consumption_tier_1! += pdr.calc_data.consumption_tier_1 ?? 0;
        summed.calc_data!.consumption_tier_2! += pdr.calc_data.consumption_tier_2 ?? 0;
        summed.calc_data!.consumption_tier_3! += pdr.calc_data.consumption_tier_3 ?? 0;
        summed.calc_data!.consumption_tier_4! += pdr.calc_data.consumption_tier_4 ?? 0;
        summed.calc_data!.consumption_tier_5! += pdr.calc_data.consumption_tier_5 ?? 0;
        summed.calc_data!.consumption_tier_6! += pdr.calc_data.consumption_tier_6 ?? 0;
        summed.calc_data!.consumption_tier_7! += pdr.calc_data.consumption_tier_7 ?? 0;
        summed.calc_data!.consumption_tier_8! += pdr.calc_data.consumption_tier_8 ?? 0;

        summed.calc_data!.var_distribution_costs_tier1! += pdr.calc_data.var_distribution_costs_tier1 ?? 0;
        summed.calc_data!.var_distribution_costs_tier2! += pdr.calc_data.var_distribution_costs_tier2 ?? 0;
        summed.calc_data!.var_distribution_costs_tier3! += pdr.calc_data.var_distribution_costs_tier3 ?? 0;
        summed.calc_data!.var_distribution_costs_tier4! += pdr.calc_data.var_distribution_costs_tier4 ?? 0;
        summed.calc_data!.var_distribution_costs_tier5! += pdr.calc_data.var_distribution_costs_tier5 ?? 0;
        summed.calc_data!.var_distribution_costs_tier6! += pdr.calc_data.var_distribution_costs_tier6 ?? 0;
        summed.calc_data!.var_distribution_costs_tier7! += pdr.calc_data.var_distribution_costs_tier7 ?? 0;
        summed.calc_data!.var_distribution_costs_tier8! += pdr.calc_data.var_distribution_costs_tier8 ?? 0;

        summed.calc_data!.total_var_distribution_costs_calc! += pdr.calc_data.total_var_distribution_costs_calc ?? 0;
        summed.calc_data!.tms_gas_charges_calc! += pdr.calc_data.tms_gas_charges_calc ?? 0;
        summed.calc_data!.tax_consumption_scale1! += pdr.calc_data.tax_consumption_scale1 ?? 0;
        summed.calc_data!.tax_consumption_scale2! += pdr.calc_data.tax_consumption_scale2 ?? 0;
        summed.calc_data!.tax_consumption_scale3! += pdr.calc_data.tax_consumption_scale3 ?? 0;
        summed.calc_data!.tax_consumption_scale4! += pdr.calc_data.tax_consumption_scale4 ?? 0;
        summed.calc_data!.total_gas_taxes_calc! += pdr.calc_data.total_gas_taxes_calc ?? 0;
        summed.calc_data!.invoice_amount_net_calc! += pdr.calc_data.invoice_amount_net_calc ?? 0;
        summed.calc_data!.invoice_amount_gross_calc! += pdr.calc_data.invoice_amount_gross_calc ?? 0;
        summed.calc_data!.vat_amount_calc! += pdr.calc_data.vat_amount_calc ?? 0;
      }
    });

    return summed;
  }

  getElectricityInvoicePODSummed(invoice: ElectricityInvoice): InvoiceElectricityPODGet {
    const summedCalcData: ElectricityPODCalculationData = {
      total_consumption_calc: 0,
      price_var_f1_calc: 0,
      price_var_f2_calc: 0,
      price_var_f3_calc: 0,
      price_var_f0_calc: 0,
      price_var_p_calc: 0,
      price_var_po_calc: 0,
      network_loss_f1: 0,
      network_loss_f2: 0,
      network_loss_f3: 0,
      network_loss_f0: 0,
      network_loss_p: 0,
      network_loss_po: 0,
      total_material_costs: 0,
      energy_charge: 0,
      power_charge: 0,
      tms_charges_calc: 0,
      total_taxes_calc: 0,
      unit_pass_costs: 0,
      total_unit_costs_f1: 0,
      total_unit_costs_f2: 0,
      total_unit_costs_f3: 0,
      total_unit_costs_f0: 0,
      total_unit_costs_p: 0,
      total_unit_costs_po: 0,
      total_commodity_costs: 0,
      invoice_amount_net_calc: 0,
      file_dispute_reasons: null,
      invoice_amount_gross_calc: 0,
      vat_amount_calc: 0
    };

    const summed: InvoiceElectricityPODGet = {
      id: null,
      contract_type: null,
      tariff_code: null,
      consumption_class: null,
      month_consumption_f1: 0,
      month_consumption_f2: 0,
      month_consumption_f3: 0,
      month_consumption_f0: 0,
      month_consumption_p: 0,
      month_consumption_po: 0,
      price_type: null,
      pun_price_f1: 0,
      pun_price_f2: 0,
      pun_price_f3: 0,
      pun_price_f0: 0,
      pun_price_p: 0,
      pun_price_po: 0,
      spread_f1: 0,
      spread_f2: 0,
      spread_f3: 0,
      spread_f0: 0,
      spread_p: 0,
      spread_po: 0,
      price_var_f1: 0,
      price_var_f2: 0,
      price_var_f3: 0,
      price_var_f0: 0,
      price_var_p: 0,
      price_var_po: 0,
      price_fix_f1: 0,
      price_fix_f2: 0,
      price_fix_f3: 0,
      price_fix_p: 0,
      price_fix_po: 0,
      price_fix_f0: 0,
      network_tension: null,
      tension_factor: 0,
      power_kw_available: 0,
      power_kw_contracted: 0,
      power_kw_max_consumption: 0,
      total_material_costs: 0,
      dispatch_costs: 0,
      dispatching_factor: 0,
      market_capacity_p: 0,
      market_capacity_op: 0,
      fixed_charge: 0,
      energy_charge_price: 0,
      power_charge_price: 0,
      reactive_energy_costs: 0,
      fixed_costs: 0,
      additional_charges: 0,
      total_discount_amount: 0,
      taxrate_1: 0,
      taxrate_2: 0,
      is_price_var_f1_ok: true,
      is_price_var_f2_ok: true,
      is_price_var_f3_ok: true,
      is_price_var_f0_ok: true,
      is_price_var_p_ok: true,
      is_price_var_po_ok: true,
      is_tms_charges_ok: true,
      calc_data: summedCalcData,
      supplier: null,
      pod_id: null,
      available_capacity: 0,
      voltage: 0,
      excise_taxes: 0,
      vat_rate: 0,
      vat_amount_actual: 0,
      tms_charges: 0,
      total_taxes: 0,
      additional_note_fields: null,
      modulation_charge: 0
    };

    invoice.pods.forEach((pod) => {
      summed.month_consumption_f1! += pod.month_consumption_f1 ?? 0;
      summed.month_consumption_f2! += pod.month_consumption_f2 ?? 0;
      summed.month_consumption_f3! += pod.month_consumption_f3 ?? 0;
      summed.month_consumption_f0! += pod.month_consumption_f0 ?? 0;
      summed.month_consumption_p! += pod.month_consumption_p ?? 0;
      summed.month_consumption_po! += pod.month_consumption_po ?? 0;

      summed.pun_price_f1! += pod.pun_price_f1 ?? 0;
      summed.pun_price_f2! += pod.pun_price_f2 ?? 0;
      summed.pun_price_f3! += pod.pun_price_f3 ?? 0;
      summed.pun_price_f0! += pod.pun_price_f0 ?? 0;
      summed.pun_price_p! += pod.pun_price_p ?? 0;
      summed.pun_price_po! += pod.pun_price_po ?? 0;

      summed.spread_f1! += pod.spread_f1 ?? 0;
      summed.spread_f2! += pod.spread_f2 ?? 0;
      summed.spread_f3! += pod.spread_f3 ?? 0;
      summed.spread_f0! += pod.spread_f0 ?? 0;
      summed.spread_p! += pod.spread_p ?? 0;
      summed.spread_po! += pod.spread_po ?? 0;

      summed.price_var_f1! += pod.price_var_f1 ?? 0;
      summed.price_var_f2! += pod.price_var_f2 ?? 0;
      summed.price_var_f3! += pod.price_var_f3 ?? 0;
      summed.price_var_f0! += pod.price_var_f0 ?? 0;
      summed.price_var_p! += pod.price_var_p ?? 0;
      summed.price_var_po! += pod.price_var_po ?? 0;

      summed.price_fix_f1! += pod.price_fix_f1 ?? 0;
      summed.price_fix_f2! += pod.price_fix_f2 ?? 0;
      summed.price_fix_f3! += pod.price_fix_f3 ?? 0;
      summed.price_fix_p! += pod.price_fix_p ?? 0;
      summed.price_fix_po! += pod.price_fix_po ?? 0;
      summed.price_fix_f0! += pod.price_fix_f0 ?? 0;

      summed.tension_factor! += pod.tension_factor ?? 0;
      summed.power_kw_available! += pod.power_kw_available ?? 0;
      summed.power_kw_max_consumption! += pod.power_kw_max_consumption ?? 0;
      summed.total_material_costs! += pod.total_material_costs ?? 0;
      summed.dispatch_costs! += pod.dispatch_costs ?? 0;
      summed.dispatching_factor! += pod.dispatching_factor ?? 0;
      summed.market_capacity_p! += pod.market_capacity_p ?? 0;
      summed.market_capacity_op! += pod.market_capacity_op ?? 0;
      summed.fixed_charge! += pod.fixed_charge ?? 0;
      summed.energy_charge_price! += pod.energy_charge_price ?? 0;
      summed.power_charge_price! += pod.power_charge_price ?? 0;
      summed.reactive_energy_costs! += pod.reactive_energy_costs ?? 0;
      summed.fixed_costs! += pod.fixed_costs ?? 0;
      summed.additional_charges! += pod.additional_charges ?? 0;
      summed.total_discount_amount! += pod.total_discount_amount ?? 0;
      summed.taxrate_1! += pod.taxrate_1 ?? 0;
      summed.taxrate_2! += pod.taxrate_2 ?? 0;

      summed.available_capacity! += pod.available_capacity ?? 0;
      summed.voltage! += pod.voltage ?? 0;
      summed.excise_taxes! += pod.excise_taxes ?? 0;
      summed.vat_rate! += pod.vat_rate ?? 0;
      summed.tms_charges! += pod.tms_charges ?? 0;
      summed.total_taxes! += pod.total_taxes ?? 0;
      summed.modulation_charge! += pod.modulation_charge ?? 0;

      summed.is_price_var_f1_ok = summed.is_price_var_f1_ok && pod.is_price_var_f1_ok;
      summed.is_price_var_f2_ok = summed.is_price_var_f2_ok && pod.is_price_var_f2_ok;
      summed.is_price_var_f3_ok = summed.is_price_var_f3_ok && pod.is_price_var_f3_ok;
      summed.is_price_var_f0_ok = summed.is_price_var_f0_ok && pod.is_price_var_f0_ok;
      summed.is_price_var_p_ok = summed.is_price_var_p_ok && pod.is_price_var_p_ok;
      summed.is_price_var_po_ok = summed.is_price_var_po_ok && pod.is_price_var_po_ok;
      summed.is_tms_charges_ok = summed.is_tms_charges_ok && pod.is_tms_charges_ok;

      if (pod.calc_data) {
        summed.calc_data!.total_consumption_calc! += pod.calc_data.total_consumption_calc ?? 0;
        summed.calc_data!.price_var_f1_calc! += pod.calc_data.price_var_f1_calc ?? 0;
        summed.calc_data!.price_var_f2_calc! += pod.calc_data.price_var_f2_calc ?? 0;
        summed.calc_data!.price_var_f3_calc! += pod.calc_data.price_var_f3_calc ?? 0;
        summed.calc_data!.price_var_f0_calc! += pod.calc_data.price_var_f0_calc ?? 0;
        summed.calc_data!.price_var_p_calc! += pod.calc_data.price_var_p_calc ?? 0;
        summed.calc_data!.price_var_po_calc! += pod.calc_data.price_var_po_calc ?? 0;
        summed.calc_data!.network_loss_f1! += pod.calc_data.network_loss_f1 ?? 0;
        summed.calc_data!.network_loss_f2! += pod.calc_data.network_loss_f2 ?? 0;
        summed.calc_data!.network_loss_f3! += pod.calc_data.network_loss_f3 ?? 0;
        summed.calc_data!.network_loss_f0! += pod.calc_data.network_loss_f0 ?? 0;
        summed.calc_data!.network_loss_p! += pod.calc_data.network_loss_p ?? 0;
        summed.calc_data!.network_loss_po! += pod.calc_data.network_loss_po ?? 0;
        summed.calc_data!.total_material_costs! += pod.calc_data.total_material_costs ?? 0;
        summed.calc_data!.energy_charge! += pod.calc_data.energy_charge ?? 0;
        summed.calc_data!.power_charge! += pod.calc_data.power_charge ?? 0;
        summed.calc_data!.tms_charges_calc! += pod.calc_data.tms_charges_calc ?? 0;
        summed.calc_data!.total_taxes_calc! += pod.calc_data.total_taxes_calc ?? 0;
        summed.calc_data!.unit_pass_costs! += pod.calc_data.unit_pass_costs ?? 0;
        summed.calc_data!.total_unit_costs_f1! += pod.calc_data.total_unit_costs_f1 ?? 0;
        summed.calc_data!.total_unit_costs_f2! += pod.calc_data.total_unit_costs_f2 ?? 0;
        summed.calc_data!.total_unit_costs_f3! += pod.calc_data.total_unit_costs_f3 ?? 0;
        summed.calc_data!.total_unit_costs_f0! += pod.calc_data.total_unit_costs_f0 ?? 0;
        summed.calc_data!.total_unit_costs_p! += pod.calc_data.total_unit_costs_p ?? 0;
        summed.calc_data!.total_unit_costs_po! += pod.calc_data.total_unit_costs_po ?? 0;
        summed.calc_data!.total_commodity_costs! += pod.calc_data.total_commodity_costs ?? 0;
        summed.calc_data!.invoice_amount_net_calc! += pod.calc_data.invoice_amount_net_calc ?? 0;
        summed.calc_data!.invoice_amount_gross_calc! += pod.calc_data.invoice_amount_gross_calc ?? 0;
        summed.calc_data!.vat_amount_calc! += pod.calc_data.vat_amount_calc ?? 0;
      }
    });

    return summed;
  }

  getElectricityDisputeReasonsSummary(invoice: ElectricityInvoice | InvoiceElectricityAndCompanyGet): string {
    const lines: string[] = [];
    let totalDispute = 0;

    for (const pod of invoice.pods) {
      if (pod.calc_data?.file_dispute_reasons) {
        for (const reason of pod.calc_data.file_dispute_reasons) {
          const details = reason['details'];
          const type = reason['type'] as string;
          const translationKey = `admin.dispute_reasons.${type}`;
          const typeLabel = this.translocoService.translate(translationKey) !== translationKey
            ? this.translocoService.translate(translationKey)
            : type;

          if (type === 'vat_rate_mismatch') {
            lines.push(`${typeLabel}: ${this.translocoService.translate('admin.dispute_reasons.vat_description', {
              atecoCode: details['ateco_code'],
              expectedVatRate: details['expected_vat_rate'],
              invoiceVatRate: details['invoice_vat_rate']
            })}`);
          } else {
            totalDispute += details['difference'] ?? 0;
            lines.push(`${typeLabel}: ${this.translocoService.translate('admin.dispute_reasons.description', {
              invoice: details['invoice']?.toFixed(2),
              calculated: details['calculated']?.toFixed(2),
              difference: details['difference']?.toFixed(2),
              differencePercentage: details['difference_percentage']
            })}`);
          }
        }
      }
    }

    if (lines.length === 0) return '';
    lines.push(this.translocoService.translate('admin.dispute_reasons.total', { total: totalDispute.toFixed(2) }));
    return lines.join('; ');
  }

  getGasDisputeReasonsSummary(invoice: GasInvoice | InvoiceGasAndCompanyGet): string {
    const lines: string[] = [];
    let totalDispute = 0;

    for (const pdr of invoice.pdrs) {
      if (pdr.calc_data?.file_dispute_reasons) {
        for (const reason of pdr.calc_data.file_dispute_reasons) {
          const details = reason['details'];
          const type = reason['type'] as string;
          const translationKey = `admin.dispute_reasons.${type}`;
          const typeLabel = this.translocoService.translate(translationKey) !== translationKey
            ? this.translocoService.translate(translationKey)
            : type;

          if (type === 'vat_rate_mismatch') {
            lines.push(`${typeLabel}: ${this.translocoService.translate('admin.dispute_reasons.vat_description', {
              atecoCode: details['ateco_code'],
              expectedVatRate: details['expected_vat_rate'],
              invoiceVatRate: details['invoice_vat_rate']
            })}`);
          } else {
            totalDispute += details['difference'] ?? 0;
            lines.push(`${typeLabel}: ${this.translocoService.translate('admin.dispute_reasons.description', {
              invoice: details['invoice']?.toFixed(2),
              calculated: details['calculated']?.toFixed(2),
              difference: details['difference']?.toFixed(2),
              differencePercentage: details['difference_percentage']
            })}`);
          }
        }
      }
    }

    if (lines.length === 0) return '';
    lines.push(this.translocoService.translate('admin.dispute_reasons.total', { total: totalDispute.toFixed(2) }));
    return lines.join('; ');
  }

  isSpreadMissing(invoice: InvoiceElectricityAndCompanyGet | ElectricityInvoice): boolean {
    const spreadPriceTypes = new Set<string>([
      PriceType.VAR,
      PriceType.VAR_PUN,
    ]);

    const pods = invoice.pods ?? [];
    if (pods.length === 0) return true;

    const isMissing = (v: unknown) => v === null || v === undefined;

    return pods.some((pod) => {
      const priceType = pod.price_type;

      if (!priceType || !spreadPriceTypes.has(priceType)) return false;

      switch (pod.contract_type) {
        case ContractType.MONO:
          return isMissing(pod.spread_f0);

        case ContractType.FASCIA:
          return (
            isMissing(pod.spread_f1) ||
            isMissing(pod.spread_f2) ||
            isMissing(pod.spread_f3)
          );

        case ContractType.PICCO:
          return isMissing(pod.spread_p) || isMissing(pod.spread_po);

        default:
          return true;
      }
    });
  }
}
